import {Button} from "@zendeskgarden/react-buttons";
import {Dropdown, Field, Item, Label, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input as FormInput, Label as FormLabel} from "@zendeskgarden/react-forms";
import {memo, ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import {
    AllPropertyForecastLocksQuery,
    useLoadWorksheetLinesQuery,
    useRemoveWorksheetDriverDataMutation,
    useSetWorksheetLinesStatusMutation,
    useUpdateWorksheetDriverDataMutation,
    VersionType,
    WorksheetDriverLineUpdateInput
} from "../../../__generated__/generated_types";
import {WsBulkDriverNoteModal} from "../../../components/driver-note/BulkDriverNoteModal";
import {WsDriverNoteModal} from "../../../components/driver-note/DriverNoteModal";
import VZSaveStatus from "../../../components/vz-ui/vz-save-status/VZSaveStatus";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {round, roundArray} from "../../../utils/math";
import {getMonths} from "../../../utils/months-helper";
import {
    CopyWorksheetLineItemAssumptionsModal,
    CopyWorksheetLineItemSourceDriver
} from "./CopyWorksheetLineItemAssumptionsModal";
import {CreateLineItemTooltipModal} from "./CreateLineItemTooltipModal";
import {DeleteLineItemsModal} from "./DeleteLineItemsModal";
import {addToArray, cellTypeToNumber, filterSelectedLineItems, getStatusText, GroupedLineItem, toLockLookup} from "./logic";
import {RenameLineItemsModal} from "./RenameLineItemsModal";
import {SetStatusModal} from "./SetStatusModal";
import {
    BasicSimpleHoTRow,
    SimpleHoTCellStyle,
    SimpleHoTChange,
    SimpleHoTRow,
    SimpleHoTWrapper
} from "./SimpleHoTWrapper";
import {SimpleHoTWrapperControls} from "./SimpleHoTWrapperControls";
import * as css from "./styles/LineItemsTab.module.scss";
import {LineItemManagementUploadButton} from "./csvbox/LineItemManagementUploadButton";

export interface LineItemsTabProps {
    budgetYear: number;
    locks: AllPropertyForecastLocksQuery | undefined;
    locksLoading: boolean;
    versionType: VersionType.Budget | VersionType.Reforecast;
}

const COL_TO_MONTH: Record<string, number> = {
    "values.0": 0,
    "values.1": 1,
    "values.2": 2,
    "values.3": 3,
    "values.4": 4,
    "values.5": 5,
    "values.6": 6,
    "values.7": 7,
    "values.8": 8,
    "values.9": 9,
    "values.10": 10,
    "values.11": 11
};

enum GroupByOptions {
    LineItem,
    Property,
    Account
}

const GroupByLabels = {
    [GroupByOptions.LineItem]: "Line Item",
    [GroupByOptions.Property]: "Property",
    [GroupByOptions.Account]: "Account"
};

export function LineItemsTab(props: LineItemsTabProps): ReactElement {
    const {data, loading, refetch} = useLoadWorksheetLinesQuery({
        fetchPolicy: "no-cache",
        variables: {
            budgetYear: props.budgetYear,
            versionType: props.versionType
        },
    });

    const [updateWorksheetLine, {loading: updateWorksheetLineLoading}] = useUpdateWorksheetDriverDataMutation({
        notifyOnNetworkStatusChange: true
    });
    const [setWorksheetLinesStatus, {loading: setStatusLoading}] = useSetWorksheetLinesStatusMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [removeWorksheetDriverData, {loading: removeLineItemsLoading}] = useRemoveWorksheetDriverDataMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [collectedChanges, setCollectedChanges] = useState<SimpleHoTChange[]>([]);
    const [renameModal, setRenameModal] = useState<{lineItemIds: string[], currentName: string} | null>(null);
    const [setStatusModal, setSetStatusModal] = useState<{
        lineItemIds: string[];
        itemName: string[];
        initialLocked: boolean;
        initialValuesLocked: boolean;
    } | null>(null);
    const [deleteModal, setDeleteModal] = useState<{lineItemIds: string[]} | null>(null);
    const [copyModal, setCopyModal] = useState<{
        sourceDriver: CopyWorksheetLineItemSourceDriver;
        destinationProperties: {id: string; name: string, disabled?: boolean}[];
        defaultSelectedProperties: string[],
        accounts: {id: string; label: string}[];
    } | null>(null);

    const addButtonRef = useRef<HTMLButtonElement>(null);
    const [addTooltipVisible, setAddTooltipVisible] = useState<boolean>(false);

    const {properties} = useProperties();
    const {accounts} = useChartOfAccounts();

    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [groupBy, setGroupBy] = useState<GroupByOptions>(GroupByOptions.LineItem);
    const [groupByFilter, setGroupByFilter] = useState<string | undefined>(undefined);
    const [tmpGroupByFilter, setTmpGroupByFilter] = useState<string | undefined>(undefined);

    const [editingNoteLineId, setEditingNoteLineId] = useState<string | null>(null);
    const [editingNoteLineName, setEditingNoteLineName] = useState<string | null>(null);
    const [bulkEditingNoteLineIds, setBulkEditingNoteLineIds] = useState<string[] | null>(null);
    const [bulkEditingNoteLineNames, setBulkEditingNoteLineNames] = useState<string[] | null>(null);

    useEffect(() => {
        const to = setTimeout(() => {
            setGroupByFilter(tmpGroupByFilter);
        }, 500);
        return () => clearTimeout(to);
    }, [tmpGroupByFilter]);

    const rows = useMemo<GroupedLineItem[]>(() => {
        if (loading || !data?.loadWorksheetLines || props.locksLoading) {
            return [];
        }

        const accountsMap = accounts.toIdMap(a => a.id);
        const propertyMap = properties?.toIdMap(p => p.id) ?? {};

        return data.loadWorksheetLines.map(l => {
            const property = propertyMap[l.propertyId];
            const account = accountsMap[l.destinationAccountId];

            return {
                lineItemId: l.id,
                propertyId: l.propertyId,
                destinationAccountId: l.destinationAccountId,
                description: l.description,
                locked: l.locked,
                valuesLocked: l.valuesLocked,
                values: l.values,
                propertyName: property?.name ?? l.propertyId,
                accountLabel: account ? `${account.accountNumber} - ${account.name}` : l.destinationAccountId,
            };
        });
    }, [data, loading, props.locksLoading, properties, accounts]);

    const groupedRows = useMemo(() => {
        let sortedRows = rows;

        if(groupBy === GroupByOptions.LineItem) {
            sortedRows = rows.sortBy("description", "propertyName", "accountLabel");
        } else if(groupBy === GroupByOptions.Property) {
            sortedRows = rows.sortBy("propertyName", "description", "accountLabel");
        } else {
            sortedRows = rows.sortBy("accountLabel", "description", "propertyName");
        }

        const grouped = sortedRows.groupBy(d => {
            if(groupBy === GroupByOptions.LineItem) {
                return d.description;
            } else if(groupBy === GroupByOptions.Property) {
                return d.propertyName;
            } else {
                return d.accountLabel;
            }
        });

        if(groupByFilter) {
            const filtered: typeof grouped = {};
            for(const [key, val] of Object.entries(grouped)) {
                if(key.toLowerCase().includes(groupByFilter.toLowerCase())) {
                    filtered[key] = val;
                }
            }
            return filtered;
        }
        return grouped;
    }, [rows, groupBy, groupByFilter]);

    const [simpleHoTRows, setSimpleHoTRows] = useState<SimpleHoTRow[]>([]);

    useEffect(() => {
        const hotRows: BasicSimpleHoTRow[] = [];
        const sortedKeys = Object.keys(groupedRows).sort();
        for(const key of sortedKeys) {
            const val = groupedRows[key];
            if(!val) {
                continue;
            }
            const parentSum: number[] = new Array(12).fill(0);
            const children: BasicSimpleHoTRow[] = [];
            let lockedCount = 0;
            let valuesLockedCount = 0;
            for(const child of val) {
                const childVals = child.values.map(v => v ?? 0);
                addToArray(parentSum, childVals);
                const status = [
                    child.locked ? "Edit-Only" : undefined,
                    child.valuesLocked ? "Read-Only" : undefined
                ].filter(Boolean).join(", ");
                if(child.locked) {
                    lockedCount++;
                }
                if(child.valuesLocked) {
                    valuesLockedCount++;
                }
                children.push({
                    isCompound: false,
                    values: {
                        label: key,
                        property: child.propertyName,
                        account: child.accountLabel,
                        status: status,
                        values: childVals,
                        lineItem: child.description,
                    },
                    extraData: {
                        lineItem: child
                    }
                });
            }

            let status: string | string[] = [];

            if (lockedCount > 0) {
                status.push(lockedCount === val.length ? "Edit-Only" : "Partially Edit-Only");
            }

            if (valuesLockedCount > 0) {
                status.push(valuesLockedCount === val.length ? "Read-Only" : "Partially Read-Only");
            }

            status = status.join(", ");

            hotRows.push({
                isCompound: false,
                children: children,
                values: {
                    label: key,
                    property: null,
                    account: null,
                    status: status,
                    values: roundArray(parentSum, 2)
                },
                summaryUpdate: change => {
                    const row = change.parent as BasicSimpleHoTRow;
                    const valuesIdx = COL_TO_MONTH[change.col];
                    if(valuesIdx === undefined) {
                        return undefined;
                    }
                    const vals = (row.values as {values: number[]}).values;
                    const val = vals[valuesIdx];
                    if(val === undefined) {
                        return undefined;
                    }

                    if(row.children && row.children.length > 0) {
                        const newVal = cellTypeToNumber(change.newValue);
                        const oldVal = cellTypeToNumber(change.previousValue);
                        if(oldVal !== null && newVal !== null) {
                            return round(val + newVal - oldVal, 2);
                        }
                    }
                    return undefined;
                }
            });
        }
        setSimpleHoTRows(hotRows);
    }, [groupedRows]);

    const locksLookup = useMemo<Set<string>>(() => {
        return toLockLookup(props.versionType, props.locks);
    }, [props.versionType, props.locks]);

    useEffect(() => {
        if(updateWorksheetLineLoading || collectedChanges.length === 0) {
            return;
        }

        // This takes care of having more than one change per month.
        // The value of the most recent change is used.
        const byId: Record<string, WorksheetDriverLineUpdateInput> = {};
        for(const change of collectedChanges) {
            const lineItem = change.row.extraData?.["lineItem"] as GroupedLineItem;
            if(lineItem && typeof change.col === "string") {
                const lineId = lineItem.lineItemId;
                const line = byId[lineId];
                const month = COL_TO_MONTH[change.col];

                if(month !== undefined) {
                    if(!line) {
                        byId[lineId] = {
                            lineId: lineId,
                            lineName: lineItem.description,
                            lineValues: [
                                {
                                    monthIndex: month,
                                    value: change.newValue?.toString()
                                }
                            ]
                        };
                    } else {
                        const val = line.lineValues.find(v => v.monthIndex === month);
                        if(val) {
                            val.value = change.newValue?.toString();
                        } else {
                            line.lineValues.push({
                                monthIndex: month,
                                value: change.newValue?.toString()
                            });
                        }
                    }
                }
            }
        }
        setCollectedChanges([]);

        updateWorksheetLine({
            variables: {
                creates: [],
                updates: Object.values(byId),
                budgetYear: props.budgetYear
            }
        }).then(res => {
            if(res.data?.updateWorksheetDriverData) {
                setLastSaved(new Date());
                return;
            }
            toast.error("Failed to save Line Items");
        }).catch(() => {
            toast.error("Failed to save Line Items.");
        });
    }, [updateWorksheetLineLoading, collectedChanges]);

    function handleChange(changes: SimpleHoTChange[]): void {
        setCollectedChanges([...collectedChanges, ...changes]);
    }

    function getDataProperty(col: number, groupBy: GroupByOptions): string {
        if(groupBy === GroupByOptions.LineItem) {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "property";
            } else {
                return "account";
            }
        } else if(groupBy === GroupByOptions.Property) {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "lineItem";
            } else {
                return "account";
            }
        } else {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "lineItem";
            } else {
                return "property";
            }
        }
    }

    function getColumnLabel(col: number, groupBy: GroupByOptions): string {
        if(groupBy === GroupByOptions.LineItem) {
            if(col === 0) {
                return "Line Item";
            } else if(col === 1) {
                return "Property";
            } else {
                return "Account";
            }
        } else if(groupBy === GroupByOptions.Property) {
            if(col === 0) {
                return "Property";
            } else if(col === 1) {
                return "Line Item";
            } else {
                return "Account";
            }
        } else {
            if(col === 0) {
                return "Account";
            } else if(col === 1) {
                return "Line Item";
            } else {
                return "Property";
            }
        }
    }

    const months = getMonths(properties?.[0] ?? undefined);
    return (
        <div className={css.lineItemsTabWrapper}>
            <SimpleHoTWrapperControls>
                <div className={css.lineItemsTabControls}>
                    <div className={css.filters}>
                        <Dropdown
                                selectedItem={groupBy}
                                onSelect={item => setGroupBy(item as GroupByOptions)}
                        >
                            <Field>
                                <Label>Primary Column</Label>
                                <Select>{GroupByLabels[groupBy]}</Select>
                            </Field>
                            <Menu>
                                <Item value={GroupByOptions.LineItem}>{GroupByLabels[GroupByOptions.LineItem]}</Item>
                                <Item value={GroupByOptions.Property}>{GroupByLabels[GroupByOptions.Property]}</Item>
                                <Item value={GroupByOptions.Account}>{GroupByLabels[GroupByOptions.Account]}</Item>
                            </Menu>
                        </Dropdown>
                        <FormField>
                            <FormLabel>Filter Primary Column</FormLabel>
                            <FormInput
                                    value={tmpGroupByFilter ?? ""}
                                    onChange={e => setTmpGroupByFilter(e.target.value ?? undefined)}
                            />
                        </FormField>
                    </div>
                    <div className={css.actions}>
                        <div style={{display: "flex", gap: "0.5rem"}}>
                            <Button size="small" ref={addButtonRef} onClick={() => setAddTooltipVisible(!addTooltipVisible)}>
                                Add Line Item
                            </Button>
                            <LineItemManagementUploadButton />
                        </div>
                        <div className={css.saveStatus}>
                            <VZSaveStatus isSaving={collectedChanges.length > 0 || updateWorksheetLineLoading} lastSaved={lastSaved} />
                        </div>
                    </div>
                </div>
            </SimpleHoTWrapperControls>
            <MemoizedSimpleHoTWrapper
                withControls
                openSectionsPersistOnRender
                dataLoading={loading || props.locksLoading}
                rows={simpleHoTRows}
                nestedRows={true}
                columns={[
                    {data: getDataProperty(0, groupBy)},
                    {data: getDataProperty(1, groupBy)},
                    {data: getDataProperty(2, groupBy)},
                    {data: "status"},
                    {data: "values.0", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.1", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.2", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.3", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.4", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.5", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.6", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.7", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.8", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.9", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.10", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    {data: "values.11", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                ]}
                cellStyler={(_row, col, rowData) => {
                    const style: SimpleHoTCellStyle = {};
                    const isParentRow = rowData.children !== undefined;
                    if([0, 1, 2, 3].includes(col) || isParentRow) {
                        style.isReadOnly = true;
                    }

                    const lineItem = rowData.extraData?.["lineItem"] as GroupedLineItem;
                    if(lineItem) {
                        const lock = locksLookup.has(lineItem.propertyId);
                        if(lock) {
                            style.isReadOnly = true;

                            if(col === 0) {
                                style.cssClasses = ["lockedColumn"];
                            }
                        }
                    }

                    return style;
                }}
                contextMenu={{
                    "copyToOtherProperties": {
                        name: "Copy To Other Properties/Accounts",
                        hidden: (selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            if (!(selectedRows.size === 1 && selectedCols.size === 1 && isValidCol)) {
                                // Copy is possible when right click on non-assumption data column and single row selected
                                return true;
                            }
                            return false;
                        },
                        disabled: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return true;
                            }
                            const row = selectedRowData[0]?.rowData;
                            const lineItem = row?.extraData?.["lineItem"] as GroupedLineItem | undefined;
                            if (!lineItem) {
                                return true;
                            }
                            return false;
                        },
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return;
                            }
                            const row = selectedRowData[0]?.rowData;
                            const lineItem = row?.extraData?.["lineItem"] as GroupedLineItem | undefined;
                            if (!lineItem) {
                                return;
                            }

                            const rowValues = row?.values["values"];

                            if (!rowValues || !Array.isArray(rowValues)) {
                                return;
                            }

                            const allDriversWithTheSameName = rows.filter(driver => driver.description === lineItem.description);

                            if (allDriversWithTheSameName === undefined) {
                                return;
                            }

                            const matchingUnlockedDriverPropertyIds = allDriversWithTheSameName.filter(driver =>
                                driver.description === lineItem.description
                                && !locksLookup.has(driver.propertyId)
                            ).map(p => p.propertyId).dedupe();

                            const propertySelectOptions = (properties ?? []).map(p => ({
                                id: p.id,
                                name: p.name,
                                disabled: locksLookup.has(p.id),
                            }));

                            if (propertySelectOptions.every(p => p.disabled === true)) {
                                toast.error("All properties are locked for this period");
                                return;
                            }

                            setCopyModal({
                                sourceDriver: {
                                    driverId: lineItem.lineItemId,
                                    name: lineItem.description,
                                    propertyId: lineItem.propertyId,
                                    propertyName: lineItem.propertyName,
                                    destinationAccountId: lineItem.destinationAccountId,
                                    accountLabel: lineItem.accountLabel,
                                    status: getStatusText(lineItem),
                                    locked: lineItem.locked,
                                    valuesLocked: lineItem.valuesLocked,
                                    monthlyValues: rowValues.map(x => x == null ? 0 : Number(x)),
                                },
                                destinationProperties: propertySelectOptions,
                                defaultSelectedProperties: matchingUnlockedDriverPropertyIds,
                                accounts: accounts.map(a => ({id: a.id, label: `${a.accountNumber} ${a.name}`})),
                            });
                        }
                    },
                    "rename": {
                        name: "Rename Line Item(s)",
                        hidden: (selectedRows, selectedCols, _selectedRowData) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(
                                selectedRows.size === 1 &&
                                selectedCols.size === 1 &&
                                isValidCol
                            );
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return;
                            }
                            const selectedItem = selectedRowData[0];
                            if (!selectedItem) {
                                return;
                            }

                            let currentName = "";
                            const itemIds: string[] = [];
                            if(selectedItem.rowData.children && selectedItem.rowData.children.length > 0) {
                                for(const child of selectedItem.rowData.children) {
                                    const lineItem = child.extraData?.["lineItem"] as GroupedLineItem;
                                    if(lineItem) {
                                        itemIds.push(lineItem.lineItemId);
                                        currentName = lineItem.description;
                                    }
                                }
                            } else {
                                const lineItem = selectedItem.rowData.extraData?.["lineItem"] as GroupedLineItem;
                                if(lineItem) {
                                    itemIds.push(lineItem.lineItemId);
                                    currentName = lineItem.description;
                                }
                            }

                            setRenameModal({
                                currentName: currentName,
                                lineItemIds: itemIds
                            });
                        }
                    },
                    "delete": {
                        name: "Delete Line Item(s)",
                        hidden: (_selectedRows, selectedCols, _selectedRowData) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedLineItemIds = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const lineItem = child.extraData?.["lineItem"] as GroupedLineItem;
                                        if (lineItem && !selectedLineItemIds.has(lineItem.lineItemId)) {
                                            selectedLineItemIds.add(lineItem.lineItemId);
                                        }
                                    }
                                } else {
                                    const lineItem = rowData.extraData?.["lineItem"] as GroupedLineItem;
                                    if (lineItem && !selectedLineItemIds.has(lineItem.lineItemId)) {
                                        selectedLineItemIds.add(lineItem.lineItemId);
                                    }
                                }
                            }

                            setDeleteModal({
                                lineItemIds: Array.from(selectedLineItemIds)
                            });
                        }
                    },
                    "changeStatus": {
                        name: "Set Non-Admin Access",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedLineItemIds = new Set<string>();
                            const lineItems: GroupedLineItem[] = [];
                            const itemNames = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const lineItem = child.extraData?.["lineItem"] as GroupedLineItem;
                                        if (lineItem && !selectedLineItemIds.has(lineItem.lineItemId)) {
                                            selectedLineItemIds.add(lineItem.lineItemId);
                                            lineItems.push(lineItem);
                                            itemNames.add(lineItem.description);
                                        }
                                    }
                                } else {
                                    const lineItem = rowData.extraData?.["lineItem"] as GroupedLineItem;
                                    if (lineItem && !selectedLineItemIds.has(lineItem.lineItemId)) {
                                        selectedLineItemIds.add(lineItem.lineItemId);
                                        lineItems.push(lineItem);
                                        itemNames.add(lineItem.description);
                                    }
                                }
                            }

                            const allLocked = lineItems.length > 0 && lineItems.every(l => l.locked);
                            const allValuesLocked = lineItems.length > 0 && lineItems.every(l => l.valuesLocked);

                            setSetStatusModal({
                                lineItemIds: Array.from(selectedLineItemIds),
                                itemName: Array.from(itemNames),
                                initialLocked: allLocked,
                                initialValuesLocked: allValuesLocked
                            });
                        }
                    },
                    "viewNote": {
                        name: "View/Update Line Item Note(s)",
                        hidden: (_selectedRows, selectedCols, _selectedRowData) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedLineItemIds = new Set<string>();
                            const selectedLineItemNames = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const lineItem = child.extraData?.["lineItem"] as GroupedLineItem;
                                        if (lineItem) {
                                            selectedLineItemIds.add(lineItem.lineItemId);
                                            selectedLineItemNames.add(lineItem.description);
                                        }
                                    }
                                } else {
                                    const lineItem = rowData.extraData?.["lineItem"] as GroupedLineItem;
                                    if (lineItem) {
                                        selectedLineItemIds.add(lineItem.lineItemId);
                                        selectedLineItemNames.add(lineItem.description);
                                    }
                                }
                            }

                            const idArray = Array.from(selectedLineItemIds);
                            const nameArray = Array.from(selectedLineItemNames);
                            if (idArray.length === 1) {
                                const firstRowId = idArray[0];
                                const firstRowName = nameArray[0];

                                if (firstRowId) {
                                    setEditingNoteLineId(firstRowId);
                                    setEditingNoteLineName(firstRowName ?? "");
                                }
                            } else if (idArray.length > 1) {
                                setBulkEditingNoteLineIds(idArray);
                                setBulkEditingNoteLineNames(nameArray);
                            }
                        }
                    },
                }}
                hotProps={{
                    renderAllRows: true,
                    height: "50vh",
                    colHeaders: [getColumnLabel(0, groupBy), getColumnLabel(1, groupBy), getColumnLabel(2, groupBy), "Non-Admin Access", ...months],
                    colWidths: [150, 150, 150, 150, ...(new Array(12).fill(90))],
                    fixedColumnsStart: 3,
                    autoRowSize: false,
                    autoColumnSize: false,
                    rowHeights: 40,
                    undo: false
                }}
                onChange={handleChange}
            />
            <RenameLineItemsModal
                visible={renameModal !== null}
                currentName={renameModal?.currentName ?? ""}
                lineItemIds={renameModal?.lineItemIds ?? []}
                budgetYear={props.budgetYear}
                onClose={() => setRenameModal(null)}
                onRenamed={() => {
                    setRenameModal(null);
                    refetch();
                }}
            />
            <SetStatusModal
                visible={setStatusModal !== null}
                budgetYear={props.budgetYear}
                itemName={setStatusModal?.itemName ?? []}
                itemIds={setStatusModal?.lineItemIds ?? []}
                itemLabel="Line Item"
                itemLabelPlural="Line Items"
                initialLocked={setStatusModal?.initialLocked ?? false}
                initialValuesLocked={setStatusModal?.initialValuesLocked ?? false}
                submitting={setStatusLoading}
                onSubmit={async (permissionLevel) => {
                    const result = await setWorksheetLinesStatus({
                        variables: {
                            ids: setStatusModal?.lineItemIds ?? [],
                            budgetYear: props.budgetYear,
                            locked: permissionLevel === "editOnly",
                            valuesLocked: permissionLevel === "readOnly",
                        },
                    });
                    return !!result.data?.setWorksheetLinesStatus;
                }}
                onClose={() => setSetStatusModal(null)}
                onStatusSet={() => {
                    setSetStatusModal(null);
                    refetch();
                }}
            />
            {addTooltipVisible && addButtonRef.current &&
                <CreateLineItemTooltipModal
                    budgetYear={props.budgetYear}
                    versionType={props.versionType}
                    properties={properties ?? []}
                    locksLookup={locksLookup}
                    referenceElement={addButtonRef.current}
                    onClose={() => setAddTooltipVisible(false)}
                    onCreated={() => {
                        setAddTooltipVisible(false);
                        refetch();
                    }}
                />
            }

            <WsDriverNoteModal
                lineName={editingNoteLineName}
                lineId={editingNoteLineId}
                onSave={() => setEditingNoteLineId(null)}
                onClose={() => setEditingNoteLineId(null)}
            />

            <WsBulkDriverNoteModal
                lineNames={bulkEditingNoteLineNames}
                lineIds={bulkEditingNoteLineIds}
                onSave={() => setBulkEditingNoteLineIds(null)}
                onClose={() => setBulkEditingNoteLineIds(null)}
            />

            <DeleteLineItemsModal
                visible={deleteModal !== null}
                itemIds={deleteModal?.lineItemIds ?? []}
                itemLabel="Line Item"
                itemLabelPlural="Line Items"
                submitting={removeLineItemsLoading}
                onDelete={async () => {
                    const result = await removeWorksheetDriverData({
                        variables: {
                            lineIds: deleteModal?.lineItemIds ?? [],
                            budgetYear: props.budgetYear,
                        },
                    });
                    return !!result.data?.removeWorksheetDriverData;
                }}
                onClose={() => setDeleteModal(null)}
                onDeleted={() => {
                    const count = deleteModal?.lineItemIds.length ?? 0;
                    const label = count === 1 ? "line item" : "line items";
                    toast.success(`Deleted ${count} ${label}`);
                    setDeleteModal(null);
                    refetch();
                }}
                onDeleteFailed={() => {
                    toast.error("Failed to delete line items");
                }}
            />

            {copyModal && (
                <CopyWorksheetLineItemAssumptionsModal
                    visible={copyModal !== null}
                    budgetYear={props.budgetYear}
                    versionType={props.versionType}
                    sourceDriver={copyModal.sourceDriver}
                    destinationProperties={copyModal.destinationProperties}
                    defaultSelectedProperties={copyModal.defaultSelectedProperties}
                    accounts={copyModal.accounts}
                    onClose={() => setCopyModal(null)}
                    onCopied={() => {
                        setCopyModal(null);
                        refetch();
                    }}
                />
            )}
        </div>
    );
}

const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    // Everything but rows is static.
    return prevProps.rows === nextProps.rows;
});
